/**
 * PollCreatorService (Pure) - Pure business logic for poll creation
 * Optimized for testability and isolation.
 */

import { handleError, ErrorFactory, logError } from "../lib/error-handling";
import { formatDateLocal } from "../lib/date-utils";
import i18n, { localeTag } from "../i18n";
import { resolveExpiresAt } from "../lib/pollExpiration";

// Interface pour les données de création d'un sondage
// Recopiée de usePolls pour éviter la dépendance circulaire
export interface PollData {
  type: "date" | "form";
  title: string;
  description: string | null;
  selectedDates?: string[];
  timeSlotsByDate?: Record<string, TimeSlot[]>;
  participantEmails?: string[];
  settings?: {
    timeGranularity?: number;
    allowAnonymousVotes?: boolean;
    allowMaybeVotes?: boolean;
    sendNotifications?: boolean;
    resultsVisibility?: "public" | "participants" | "creator";
    mergeMode?: boolean;
    weekendGroupingEnabled?: boolean;
  };
  questions?: any[]; // Pour les FormPolls
}

export interface TimeSlot {
  hour: number;
  minute: number;
  enabled: boolean;
  label: string;
  duration?: number;
}

export interface DateGroup {
  dates: string[];
  label: string;
  type: "weekend" | "range" | "custom";
}

export interface PollCreationState {
  selectedDates: string[];
  currentMonth: Date;
  calendarConnected: boolean;
  pollTitle: string;
  participantEmails: string | string[];
  showTimeSlots: boolean;
  timeSlots: TimeSlot[]; // Default/Uniform slots
  timeSlotsByDate?: Record<string, TimeSlot[]>; // Specific slots per date
  notificationsEnabled: boolean;
  userEmail: string;
  showCalendarConnect: boolean;
  showShare: boolean;
  showDescription: boolean;
  emailErrors: string[];
  showExtendedHours: boolean;
  timeGranularity: number;
  showGranularitySettings: boolean;
  showCalendarConnection: boolean;
  pollLinkCopied: boolean;
  needsTimeSlotRecalculation?: boolean; // Flag pour recalculer les créneaux
  dateGroups?: DateGroup[];
  expirationDays: number;
  allowMaybeVotes: boolean;
  allowAnonymousVotes: boolean;
  showExpirationSettings: boolean;
  showSettingsPanel: boolean;
  mergeMode: boolean; // Mode fusion pour regrouper les créneaux consécutifs
  weekendGroupingEnabled: boolean; // Regroupe les paires samedi-dimanche en une option "Week-end"
}

export interface PollEditData {
  id: string;
  title: string;
  settings?: {
    selectedDates?: string[];
    showTimeSlots?: boolean;
    participantEmails?: string;
    timeGranularity?: number;
    expirationDays?: number;
    timeSlotsByDate?: Record<string, TimeSlot[]>;
  };
  options?: Array<{
    option_date?: string;
  }>;
}

export class PollCreatorService {
  /**
   * Réglages avancés initiaux de l'éditeur de sondage de dates (état
   * `advancedSettings` de PollCreator). Source unique : l'éditeur les copie,
   * et le test de contrat de `lib/ai-poll/toPollDataJson` s'en sert pour
   * vérifier qu'un sondage créé par une IA a la même forme.
   */
  static readonly DEFAULT_ADVANCED_SETTINGS: Readonly<Record<string, unknown>> =
    Object.freeze({
      showLogo: true,
      requireAuth: false,
      allowEditAfterSubmit: true,
      sendEmailCopy: false,
      // `resultsCopyEmail` et non plus `emailForCopy` : c'est la clé que le
      // formulaire écrit (PollSettingsForm) et que le trigger lit désormais
      // pour router la notification de vote (20260816123000).
      resultsCopyEmail: "",
      // Défaut ON (décision 2026-07-24) : le trigger respecte désormais ce
      // switch (migration 20260724090000) — false couperait les notifications
      // de tous les nouveaux sondages.
      sendNotifications: true,
      resultsVisibility: "public",
    });

  /**
   * SOURCE UNIQUE de l'objet `settings` persisté pour un sondage de dates.
   *
   * ⚠️ Tout champ de settings DOIT être ajouté ICI, jamais recopié à la main
   * dans un appel à createPoll/updatePoll. Historique : le bug "mergeMode perdu
   * au vote" (01/06/2026) venait de `PollCreator.handleFinalize` qui construisait
   * son propre objet settings sans `mergeMode`. Centraliser supprime cette classe
   * de bug : un nouveau champ = une seule ligne ici, partagée par tous les chemins
   * (création réelle, brouillon sessionStorage, update).
   *
   * `advancedSettings` (PollSettingsForm) est spread AVANT les champs protégés
   * (mergeMode) pour ne pas les écraser.
   */
  static buildPollSettings(
    state: PollCreationState,
    advancedSettings: Record<string, any> = {},
  ): Record<string, any> {
    return {
      selectedDates: state.selectedDates, // dupliqué dans settings pour la validation
      timeGranularity: state.timeGranularity,
      allowAnonymousVotes: true,
      allowMaybeVotes: true,
      sendNotifications: state.notificationsEnabled,
      maxResponses: advancedSettings.maxResponses,
      // Settings avancés (audienceMode, resultsVisibility, showLogo, etc.)
      ...advancedSettings,
      // Expiration : APRÈS le spread — jamais avant le lendemain de la
      // dernière date proposée ; par défaut, ce lendemain (cf. pollExpiration).
      // Sans date proposée, ancien comportement (création + expirationDays).
      expiresAt:
        resolveExpiresAt(state.selectedDates, advancedSettings.expiresAt) ??
        (state.expirationDays
          ? new Date(
              Date.now() + state.expirationDays * 24 * 60 * 60 * 1000,
            ).toISOString()
          : undefined),
      // Lieu : la modale de pré-publication écrit `location`, le formulaire de
      // réglages `eventLocation` — seule clé lue par les résultats, le tableau
      // de bord et les emails. La saisie de la modale (dernière étape) prime.
      eventLocation:
        (typeof advancedSettings.location === "string" &&
          advancedSettings.location.trim()) ||
        advancedSettings.eventLocation,
      // Champs protégés : APRÈS le spread pour ne pas être écrasés.
      mergeMode: state.mergeMode,
      weekendGroupingEnabled: state.weekendGroupingEnabled,
    };
  }

  /**
   * Défaut de `voteAccess` selon l'audience choisie : un sondage envoyé à des
   * adresses précises (emails/groupe) reçoit déjà des liens d'invitation
   * personnels (cf. InvitationService) — le sondage doit donc exiger cette
   * preuve, pas rester votable par n'importe qui. `link` reste public.
   * Un override explicite du créateur (radio "Qui peut voter") prime
   * toujours. Ne PAS appeler sur un sondage déjà `active` : voir le garde
   * `loadedPollStatus` dans PollCreator.tsx (decision produit 2026-08-19,
   * #6 — pas de bascule rétroactive public→invite sur un sondage qui a déjà
   * des votes).
   */
  static deriveVoteAccess(
    audienceMode: "group" | "emails" | "link",
    explicitOverride?: unknown,
  ): string {
    if (typeof explicitOverride === "string") return explicitOverride;
    return audienceMode === "link" ? "public" : "invite";
  }

  /**
   * Convert PollCreationState to PollData for saving
   */
  static convertToPollData(state: PollCreationState): PollData {
    return {
      type: "date",
      title: state.pollTitle,
      description: null,
      selectedDates: state.selectedDates,
      timeSlotsByDate: state.showTimeSlots
        ? state.timeSlotsByDate || // Use explicit slots if available
          state.selectedDates.reduce(
            // Fallback to uniform slots
            (acc, date) => {
              acc[date] = state.timeSlots;
              return acc;
            },
            {} as Record<string, typeof state.timeSlots>,
          )
        : {},
      participantEmails: Array.isArray(state.participantEmails)
        ? state.participantEmails
        : state.participantEmails
            .split(",")
            .map((email) => email.trim())
            .filter(Boolean),
      settings: this.buildPollSettings(state),
    };
  }

  /**
   * Check if poll can be finalized
   */
  static canFinalize(state: PollCreationState): boolean {
    // FIX: Si pas d'emails saisis, ignorer les erreurs email (bug du bouton désactivé)
    const hasEmails = Array.isArray(state.participantEmails)
      ? state.participantEmails.length > 0
      : state.participantEmails.trim().length > 0;
    const emailErrorsCount = hasEmails ? state.emailErrors?.length || 0 : 0;

    return (
      state.selectedDates.length > 0 &&
      state.pollTitle.trim().length > 0 &&
      emailErrorsCount === 0 // ← Ne considère les erreurs email que si des emails sont saisis
    );
  }

  /**
   * Handle poll finalization
   */
  static async handleFinalize(
    state: PollCreationState,
    createPoll: (pollData: PollData) => Promise<string>,
    onPollCreated?: (slug: string) => void,
  ): Promise<void> {
    if (!this.canFinalize(state)) {
      throw ErrorFactory.validation(
        "Poll cannot be finalized - missing required fields",
        "Veuillez remplir tous les champs requis pour finaliser le sondage.",
      );
    }

    const pollData: PollData = {
      type: "date",
      title: state.pollTitle,
      description: null,
      selectedDates: state.selectedDates,
      timeSlotsByDate: state.showTimeSlots
        ? state.timeSlotsByDate || // Use explicit slots if available
          state.selectedDates.reduce(
            // Fallback to uniform slots
            (acc, date) => {
              acc[date] = state.timeSlots;
              return acc;
            },
            {} as Record<string, typeof state.timeSlots>,
          )
        : {},
      participantEmails: Array.isArray(state.participantEmails)
        ? state.participantEmails
        : state.participantEmails
            .split(",")
            .map((email) => email.trim())
            .filter(Boolean),
      settings: this.buildPollSettings(state),
    };

    try {
      const slug = await createPoll(pollData);
      onPollCreated?.(slug);
    } catch (error) {
      throw handleError(
        error,
        {
          component: "PollCreatorService",
          operation: "handleFinalize",
        },
        "Erreur lors de la création du sondage",
      );
    }
  }

  /**
   * Toggle date selection
   */
  static toggleDate(
    dateString: string,
    setState: (updater: (prev: PollCreationState) => PollCreationState) => void,
  ): void {
    setState((prev) => ({
      ...prev,
      selectedDates: prev.selectedDates.includes(dateString)
        ? prev.selectedDates.filter((d) => d !== dateString)
        : // Trier chronologiquement (format YYYY-MM-DD, tri lexicographique
          // suffit) : les colonnes de l'éditeur d'horaires suivent l'ordre de
          // ce tableau, pas l'ordre de clic (bug signalé : dim. sélectionné
          // après sam. affichait dim. avant sam.).
          [...prev.selectedDates, dateString].sort(),
    }));
  }

  /**
   * Check if there are active time slots selected
   */
  static hasActiveTimeSlots(
    timeSlotsByDate?: Record<string, TimeSlot[]>,
  ): boolean {
    if (!timeSlotsByDate) return false;

    const selectedSlots = Object.values(timeSlotsByDate).flat();
    const enabledSlots = selectedSlots.filter((slot) => slot.enabled);

    return enabledSlots.length > 0;
  }

  /**
   * Check if granularity is compatible with selected time slots
   */
  static isGranularityCompatible(
    granularity: number,
    timeSlots: TimeSlot[],
    currentGranularity?: number,
    timeSlotsByDate?: Record<string, TimeSlot[]>,
  ): boolean {
    // TOUJOURS autoriser le changement
    return true;
  }

  /**
   * Handle granularity change
   */
  static handleGranularityChange(
    newGranularity: number,
    setState: (updater: (prev: PollCreationState) => PollCreationState) => void,
  ): void {
    setState((prev) => ({
      ...prev,
      timeGranularity: newGranularity,
      needsTimeSlotRecalculation: true, // Flag pour déclencher le recalcul
      // NE PAS fermer le menu : showGranularitySettings: false,
    }));
  }

  /**
   * Recalculate time slots with new granularity
   * PRÉSERVE les créneaux IA (duration > granularity) et applique la granularité seulement aux créneaux manuels
   */
  static async recalculateTimeSlotsWithGranularity(
    currentTimeSlotsByDate: Record<string, TimeSlot[]>,
    newGranularity: number,
  ): Promise<Record<string, TimeSlot[]>> {
    if (
      !currentTimeSlotsByDate ||
      Object.keys(currentTimeSlotsByDate).length === 0
    ) {
      return {};
    }

    const newTimeSlotsByDate: Record<string, TimeSlot[]> = {};

    Object.entries(currentTimeSlotsByDate).forEach(([date, slots]) => {
      newTimeSlotsByDate[date] = [];

      slots.forEach((slot) => {
        // Si le créneau vient de l'IA (duration > granularity ou duration multiple de granularity)
        // on le préserve intact
        if (slot.duration && slot.duration > newGranularity) {
          // Créneau IA : le préserver tel quel
          newTimeSlotsByDate[date].push({
            ...slot,
            // S'assurer que le label est correct
            label: `${slot.hour.toString().padStart(2, "0")}:${slot.minute.toString().padStart(2, "0")}`,
          });
        } else {
          // Créneau manuel : l'ajuster à la nouvelle granularité
          newTimeSlotsByDate[date].push({
            ...slot,
            duration: newGranularity,
            label: `${slot.hour.toString().padStart(2, "0")}:${slot.minute.toString().padStart(2, "0")}`,
          });
        }
      });
    });

    return newTimeSlotsByDate;
  }

  /**
   * #1476 — nombre de créneaux sélectionnés dont la durée changerait si
   * recalculateTimeSlotsWithGranularity était appliqué avec `newGranularity`.
   * Sert à demander confirmation avant d'appliquer (décision produit : « Si on
   * change, on doit prévenir et demander avant au user »). Un créneau sans
   * `duration` est considéré comme durant `currentGranularity`. Les créneaux
   * désactivés ne comptent pas : l'utilisateur ne les voit pas comme choisis.
   */
  static countSlotsChangedByGranularity(
    timeSlotsByDate: Record<string, TimeSlot[]> | undefined,
    newGranularity: number,
    currentGranularity: number,
  ): number {
    if (!timeSlotsByDate) return 0;
    let changed = 0;
    for (const slots of Object.values(timeSlotsByDate)) {
      for (const slot of slots) {
        if (!slot.enabled) continue;
        const before = slot.duration ?? currentGranularity;
        // Même règle que recalculateTimeSlotsWithGranularity
        const after =
          slot.duration && slot.duration > newGranularity
            ? slot.duration
            : newGranularity;
        if (after !== before) changed++;
      }
    }
    return changed;
  }

  private static convertTimeSlotsByDateToGemini(
    timeSlotsByDate: Record<string, TimeSlot[]>,
  ): Array<{ start: string; end: string; dates: string[] }> {
    const geminiSlots: Array<{ start: string; end: string; dates: string[] }> =
      [];

    Object.entries(timeSlotsByDate).forEach(([date, slots]) => {
      const enabledSlots = slots.filter((slot) => slot.enabled);

      if (enabledSlots.length > 0) {
        const firstSlot = enabledSlots[0];
        const lastSlot = enabledSlots[enabledSlots.length - 1];

        // Calculer l'heure de fin (début + duration du dernier slot)
        const endHour = lastSlot.hour;
        const endMinute = lastSlot.minute + (lastSlot.duration || 30);
        const adjustedEndHour = endHour + Math.floor(endMinute / 60);
        const adjustedEndMinute = endMinute % 60;

        geminiSlots.push({
          start: `${firstSlot.hour.toString().padStart(2, "0")}:${firstSlot.minute.toString().padStart(2, "0")}`,
          end: `${adjustedEndHour.toString().padStart(2, "0")}:${adjustedEndMinute.toString().padStart(2, "0")}`,
          dates: [date],
        });
      }
    });

    return geminiSlots;
  }

  /**
   * Get initial granularity state
   */
  static get initialGranularityState(): number {
    return 30; // Default 30-minute intervals
  }

  /**
   * Undo granularity change
   */
  static undoGranularityChange(
    setState: (updater: (prev: PollCreationState) => PollCreationState) => void,
  ): void {
    setState((prev) => ({
      ...prev,
      timeGranularity: this.initialGranularityState,
      showGranularitySettings: false,
    }));
  }

  /**
   * Format selected date header
   * Utilise formatDateFull pour le format complet uniforme
   *
   * @param locale - Locale app ("fr" | "en" | "es") ou tag BCP-47 ; par défaut
   *   la langue i18next active, pour que l'en-tête suive la langue de l'UI.
   */
  static formatSelectedDateHeader(
    dateString: string,
    isMobile: boolean = false,
    locale: string = i18n.language,
  ): {
    dayName: string;
    dayNumber: string;
    month: string;
    fullFormat: string;
  } {
    // Parser la date en mode local pour éviter les décalages timezone
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day); // month - 1 car JS commence à 0
    const tag = localeTag(locale || "fr");

    // Format pour mobile/tablet: acronymes des jours
    const dayNameMobile = date
      .toLocaleDateString(tag, { weekday: "short" })
      .replace(".", "");
    // Format desktop: jours complets
    const dayNameDesktop = date.toLocaleDateString(tag, {
      weekday: "long",
    });

    const dayName = isMobile ? dayNameMobile : dayNameDesktop;
    const dayNumber = date.getDate().toString();
    const monthShort = date.toLocaleDateString(tag, { month: "short" });
    // Utiliser le format uniforme pour fullFormat
    const fullFormat = date.toLocaleDateString(tag, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    return { dayName, dayNumber, month: monthShort, fullFormat };
  }

  /**
   * Get visible time slots for a date
   */
  static getVisibleTimeSlots(
    timeSlots: TimeSlot[],
    showExtendedHours: boolean,
  ): TimeSlot[] {
    const startHour = showExtendedHours ? 0 : 8;
    const endHour = showExtendedHours ? 23 : 20;

    return timeSlots.filter(
      (slot) => slot.hour >= startHour && slot.hour <= endHour,
    );
  }

  /**
   * Get time slot blocks for display
   */
  static getTimeSlotBlocks(
    timeSlots: TimeSlot[],
    granularity: number,
    mergeMode: boolean = true,
  ): Array<{
    start: TimeSlot;
    end: { hour: number; minute: number; enabled: boolean };
  }> {
    const blocks = [];
    let currentBlock = null;

    // Trier les slots par heure pour détecter les blocs contigus
    const sortedSlots = [...timeSlots].sort((a, b) => {
      const aMinutes = a.hour * 60 + a.minute;
      const bMinutes = b.hour * 60 + b.minute;
      return aMinutes - bMinutes;
    });

    for (const slot of sortedSlots) {
      if (slot.enabled) {
        if (!currentBlock) {
          // Nouveau bloc - calculer la fin en fonction de la granularité
          const endMinutes = slot.hour * 60 + slot.minute + granularity;
          const endHour = Math.floor(endMinutes / 60);
          const endMinute = endMinutes % 60;

          currentBlock = {
            start: slot,
            end: { hour: endHour, minute: endMinute, enabled: true },
          };
        } else {
          // Vérifier si ce slot est contigu au bloc actuel
          const currentEndMinutes =
            currentBlock.end.hour * 60 + currentBlock.end.minute;
          const slotStartMinutes = slot.hour * 60 + slot.minute;

          if (mergeMode && slotStartMinutes === currentEndMinutes) {
            // Mode fusion ET slot contigu - étendre le bloc
            const endMinutes = slot.hour * 60 + slot.minute + granularity;
            const endHour = Math.floor(endMinutes / 60);
            const endMinute = endMinutes % 60;

            currentBlock.end = {
              hour: endHour,
              minute: endMinute,
              enabled: true,
            };
          } else {
            // Mode normal OU slot non contigu - fermer le bloc actuel et en créer un nouveau
            blocks.push(currentBlock);

            const endMinutes = slot.hour * 60 + slot.minute + granularity;
            const endHour = Math.floor(endMinutes / 60);
            const endMinute = endMinutes % 60;

            currentBlock = {
              start: slot,
              end: { hour: endHour, minute: endMinute, enabled: true },
            };
          }
        }
      } else if (currentBlock) {
        blocks.push(currentBlock);
        currentBlock = null;
      }
    }

    if (currentBlock) {
      blocks.push(currentBlock);
    }

    return blocks;
  }

  /**
   * Handle time slot toggle for a specific date
   * Returns the new timeSlotsByDate object
   */
  static handleTimeSlotToggle(
    dateStr: string,
    hour: number,
    minute: number,
    timeSlotsByDate: Record<string, TimeSlot[]>,
    granularity: number,
    includeDuration: boolean = true,
    mergeMode: boolean = false,
  ): Record<string, TimeSlot[]> {
    const currentSlots = timeSlotsByDate[dateStr] || [];
    const clickedSlotMinutes = hour * 60 + minute;

    const existingSlotIndex = currentSlots.findIndex(
      (s) => s.hour === hour && s.minute === minute,
    );

    if (existingSlotIndex >= 0) {
      // Slot existe → Toggle enabled
      const newSlots = [...currentSlots];
      newSlots[existingSlotIndex] = {
        ...newSlots[existingSlotIndex],
        enabled: !newSlots[existingSlotIndex].enabled,
      };

      return {
        ...timeSlotsByDate,
        [dateStr]: newSlots,
      };
    } else {
      // Slot n'existe pas
      if (mergeMode) {
        // MODE FUSION : Regrouper les créneaux consécutifs automatiquement
        const enabledSlots = currentSlots.filter((s) => s.enabled);

        if (enabledSlots.length === 0) {
          // Premier créneau - créer normalement
          const newSlot: TimeSlot = {
            hour,
            minute,
            enabled: true,
            label: `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`,
          };

          if (includeDuration) {
            newSlot.duration = granularity;
          }

          return {
            ...timeSlotsByDate,
            [dateStr]: [...currentSlots, newSlot],
          };
        }

        // Chercher si le clic est adjacent à un créneau existant
        const adjacentSlots = [];
        for (const slot of enabledSlots) {
          const slotMinutes = slot.hour * 60 + slot.minute;

          // Si le créneau est juste avant ou juste après le clic
          if (Math.abs(slotMinutes - clickedSlotMinutes) === granularity) {
            adjacentSlots.push(slot);
          }
        }

        if (adjacentSlots.length > 0) {
          // Fusionner avec les créneaux adjacents
          const newSlots = [...currentSlots];

          // Activer le créneau cliqué
          const clickedIndex = newSlots.findIndex(
            (s) => s.hour === hour && s.minute === minute,
          );

          if (clickedIndex >= 0) {
            newSlots[clickedIndex] = {
              ...newSlots[clickedIndex],
              enabled: true,
            };
          } else {
            const newSlot: TimeSlot = {
              hour,
              minute,
              enabled: true,
              label: `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`,
            };

            if (includeDuration) {
              newSlot.duration = granularity;
            }

            newSlots.push(newSlot);
          }

          // S'assurer que les créneaux adjacents sont activés
          for (const adjacentSlot of adjacentSlots) {
            const adjacentIndex = newSlots.findIndex(
              (s) =>
                s.hour === adjacentSlot.hour &&
                s.minute === adjacentSlot.minute,
            );

            if (adjacentIndex >= 0) {
              newSlots[adjacentIndex] = {
                ...newSlots[adjacentIndex],
                enabled: true,
              };
            }
          }

          return {
            ...timeSlotsByDate,
            [dateStr]: newSlots,
          };
        } else {
          // Pas de créneau adjacent - créer normalement
          const newSlot: TimeSlot = {
            hour,
            minute,
            enabled: true,
            label: `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`,
          };

          if (includeDuration) {
            newSlot.duration = granularity;
          }

          return {
            ...timeSlotsByDate,
            [dateStr]: [...currentSlots, newSlot],
          };
        }
      } else {
        // MODE NORMAL - Créer nouveau slot
        const newSlot: TimeSlot = {
          hour,
          minute,
          enabled: true,
          label: `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`,
        };

        if (includeDuration) {
          newSlot.duration = granularity;
        }

        return {
          ...timeSlotsByDate,
          [dateStr]: [...currentSlots, newSlot],
        };
      }
    }
  }

  /**
   * Generate visible time slots for display
   * Returns an array of time slot objects with labels
   */
  static generateVisibleTimeSlots(
    granularity: number,
    showExtendedHours: boolean = false,
  ): Array<{ hour: number; minute: number; label: string }> {
    const slots = [];
    const startHour = 8;
    const endHour = showExtendedHours ? 23 : 20;

    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += granularity) {
        slots.push({
          hour,
          minute,
          label: `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`,
        });
      }
    }

    return slots;
  }

  /**
   * Toggle time slot for a specific date
   */
  static toggleTimeSlot(
    hour: number,
    minute: number,
    setState: (updater: (prev: PollCreationState) => PollCreationState) => void,
  ): void {
    setState((prev) => ({
      ...prev,
      timeSlots: prev.timeSlots.map((slot) =>
        slot.hour === hour && slot.minute === minute
          ? { ...slot, enabled: !slot.enabled }
          : slot,
      ),
    }));
  }

  /**
   * Initialize default time slots
   */
  static initializeTimeSlots(granularity: number = 30): TimeSlot[] {
    const slots: TimeSlot[] = [];

    // Si la granularité est >= 60, on génère des créneaux par heure
    if (granularity >= 60) {
      const hoursStep = granularity / 60;
      for (let hour = 0; hour <= 23; hour += hoursStep) {
        slots.push({
          hour,
          minute: 0,
          enabled: false,
          label: `${hour.toString().padStart(2, "0")}:00`,
        });
      }
    } else {
      // Pour les granularités < 60 minutes, on génère par minutes
      for (let hour = 0; hour <= 23; hour++) {
        for (let minute = 0; minute < 60; minute += granularity) {
          slots.push({
            hour,
            minute,
            enabled: false,
            label: `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`,
          });
        }
      }
    }

    return slots;
  }

  /**
   * Generate enabled TimeSlot entries spanning [startTime, endTime) at the
   * given granularity — used to duplicate a single "start→end" time range
   * onto every date generated by a recurring poll series (handoff: Creation
   * Intent Controls / recurring time range, 2026-07-09). Mirrors
   * `initializeTimeSlots`'s stepping logic so the resulting slots are the
   * exact shape `handleTimeSlotToggle`/mergeMode already expect — manual
   * per-date editing on top of the auto-generated slots keeps working
   * unchanged.
   *
   * @param startTime "HH:MM"
   * @param endTime "HH:MM", must be strictly after startTime
   * @throws Error if startTime/endTime are malformed or endTime <= startTime
   */
  static generateTimeSlotsForRange(
    startTime: string,
    endTime: string,
    granularity: number = 30,
  ): TimeSlot[] {
    const parse = (value: string): number => {
      const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
      if (!match) {
        throw new Error(`Invalid time format: "${value}" (expected HH:MM)`);
      }
      return Number(match[1]) * 60 + Number(match[2]);
    };

    const startMinutes = parse(startTime);
    const endMinutes = parse(endTime);
    if (endMinutes <= startMinutes) {
      throw new Error("endTime must be strictly after startTime");
    }

    const slots: TimeSlot[] = [];
    for (
      let minutes = startMinutes;
      minutes < endMinutes;
      minutes += granularity
    ) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
      slots.push({
        hour,
        minute,
        enabled: true,
        label: `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`,
        duration: granularity,
      });
    }

    return slots;
  }

  /**
   * Validate email addresses
   */
  static validateEmails(emailString: string): string[] {
    const emails = emailString
      .split(",")
      .map((email) => email.trim())
      .filter(Boolean);
    const errors: string[] = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    emails.forEach((email) => {
      if (!emailRegex.test(email)) {
        errors.push(`Email invalide: ${email}`);
      }
    });

    return errors;
  }

  /**
   * Initialize state with Gemini data
   * Robust implementation that handles partial or malformed data
   */
  static initializeWithGeminiData(initialData?: {
    title?: string;
    dates?: string[];
    participants?: string[];
    timeSlots?: unknown;
    dateGroups?: Array<{
      dates: string[];
      label: string;
      type: "weekend" | "range" | "custom";
    }>;
  }): PollCreationState {
    const baseState: PollCreationState = {
      selectedDates: [],
      currentMonth: new Date(),
      calendarConnected: false,
      pollTitle: "",
      participantEmails: "",
      showTimeSlots: false,
      timeSlots: this.initializeTimeSlots(),
      timeSlotsByDate: {}, // Initialize empty
      notificationsEnabled: true,
      userEmail: "",
      showCalendarConnect: false,
      showShare: false,
      showDescription: false,
      emailErrors: [],
      showExtendedHours: false,
      timeGranularity: 30,
      showGranularitySettings: false,
      showCalendarConnection: false,
      pollLinkCopied: false,
      expirationDays: 30,
      showExpirationSettings: false,
      showSettingsPanel: false, // Default to hidden
      allowMaybeVotes: false,
      allowAnonymousVotes: false,
      mergeMode: false, // Mode fusion désactivé par défaut
      weekendGroupingEnabled: false,
    };

    if (initialData) {
      // Robustly handle potential undefined values
      const title = initialData.title || "";
      const dates = Array.isArray(initialData.dates) ? initialData.dates : [];
      const participants = Array.isArray(initialData.participants)
        ? initialData.participants.join(", ")
        : "";

      const newState = {
        ...baseState,
        pollTitle: title,
        selectedDates: dates,
        participantEmails: participants,
        showTimeSlots:
          !!initialData.timeSlots || !!(initialData as any).timeSlotsByDate,
        dateGroups: initialData.dateGroups,
      };

      // If dates provided, set current month to first date
      if (dates.length > 0) {
        try {
          const firstDate = new Date(dates[0]);
          if (!isNaN(firstDate.getTime())) {
            newState.currentMonth = firstDate;
          }
        } catch (e) {
          // Keep default current month if parsing fails
        }
      }

      return newState;
    }

    return baseState;
  }

  /**
   * Extrait les dates d'un sondage avec plusieurs méthodes de fallback
   */
  private static extractDatesFromPoll(pollData: PollEditData): string[] {
    const pollDates: string[] = [];

    // Méthode 1: Depuis settings.selectedDates
    if (pollData.settings?.selectedDates?.length) {
      pollDates.push(...pollData.settings.selectedDates);
    }

    // Méthode 2: Depuis les options du sondage
    if (pollDates.length === 0 && pollData.options) {
      pollData.options.forEach((option) => {
        if (option.option_date && !pollDates.includes(option.option_date)) {
          pollDates.push(option.option_date);
        }
      });
    }

    // Méthode 3: Fallback - générer des dates par défaut
    if (pollDates.length === 0) {
      const today = new Date();
      for (let i = 0; i < 3; i++) {
        const futureDate = new Date(today);
        futureDate.setDate(today.getDate() + i + 1);
        pollDates.push(formatDateLocal(futureDate));
      }
    }

    return pollDates;
  }

  /**
   * Charge les données d'un sondage existant depuis localStorage (Dev/Test)
   */
  static async loadPollData(
    editPollId: string,
  ): Promise<PollCreationState | null> {
    if (typeof window === "undefined") return null;

    try {
      const existingPolls = JSON.parse(
        localStorage.getItem("dev-polls") || "[]",
      ) as unknown[];
      const pollToEdit = existingPolls.find(
        (poll: unknown) => (poll as { id?: string }).id === editPollId,
      ) as PollEditData | undefined;

      if (!pollToEdit) {
        logError(
          ErrorFactory.validation(
            "Poll not found for editing",
            "Sondage à éditer non trouvé",
          ),
          {
            component: "PollCreatorService",
            operation: "loadPollData",
            metadata: { editPollId },
          },
        );
        return null;
      }

      // Extraire les dates depuis les options du sondage
      const pollDates = this.extractDatesFromPoll(pollToEdit);
      const initialState = this.initializeWithGeminiData();

      return {
        ...initialState,
        pollTitle: pollToEdit.title || "",
        selectedDates: pollDates,
        currentMonth: pollDates[0] ? new Date(pollDates[0]) : new Date(),
        showTimeSlots: pollToEdit.settings?.showTimeSlots || false,
        participantEmails: pollToEdit.settings?.participantEmails || "",
        timeGranularity: pollToEdit.settings?.timeGranularity || 30,
        expirationDays: pollToEdit.settings?.expirationDays || 30,
        timeSlotsByDate: pollToEdit.settings?.timeSlotsByDate || {}, // Restore specific slots
      };
    } catch (error) {
      logError(
        ErrorFactory.storage(
          "Error loading poll data",
          "Erreur lors du chargement des données du sondage",
        ),
        {
          component: "PollCreatorService",
          operation: "loadPollData",
          metadata: { editPollId, error },
        },
      );
      return null;
    }
  }

  /**
   * Sauvegarde automatique dans localStorage (draft)
   */
  static saveDraft(state: PollCreationState): void {
    if (typeof window === "undefined") return;

    try {
      // Ne sauvegarder que si l'état a du contenu significatif
      if (state.pollTitle.trim() || state.selectedDates.length > 0) {
        const draftData = {
          pollTitle: state.pollTitle,
          selectedDates: state.selectedDates,
          participantEmails: state.participantEmails,
          showTimeSlots: state.showTimeSlots,
          timeGranularity: state.timeGranularity,
          expirationDays: state.expirationDays,
          timeSlotsByDate: state.timeSlotsByDate, // Save specific slots
          savedAt: new Date().toISOString(),
        };

        localStorage.setItem("doodates-draft", JSON.stringify(draftData));
      }
    } catch (error) {
      logError(
        ErrorFactory.storage(
          "Error saving draft",
          "Erreur lors de la sauvegarde automatique",
        ),
        {
          component: "PollCreatorService",
          operation: "saveDraft",
          metadata: { error },
        },
      );
    }
  }

  /**
   * Charge un brouillon depuis localStorage
   */
  static loadDraft(): Partial<PollCreationState> | null {
    if (typeof window === "undefined") return null;

    try {
      const draftData = localStorage.getItem("doodates-draft");
      if (!draftData) return null;

      const parsed = JSON.parse(draftData);

      // Vérifier que le brouillon n'est pas trop ancien (plus de 24h)
      const savedAt = new Date(parsed.savedAt);
      const now = new Date();
      const hoursDiff = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60);

      if (hoursDiff > 24) {
        localStorage.removeItem("doodates-draft");
        return null;
      }

      return {
        pollTitle: parsed.pollTitle || "",
        selectedDates: parsed.selectedDates || [],
        participantEmails: parsed.participantEmails || "",
        showTimeSlots: parsed.showTimeSlots || false,
        timeGranularity: parsed.timeGranularity || 30,
        expirationDays: parsed.expirationDays || 30,
        timeSlotsByDate: parsed.timeSlotsByDate || {}, // Restore specific slots
        mergeMode: parsed.mergeMode || false, // Mode fusion par défaut
      };
    } catch (error) {
      logError(
        ErrorFactory.storage(
          "Error loading draft",
          "Erreur lors du chargement du brouillon",
        ),
        {
          component: "PollCreatorService",
          operation: "loadDraft",
          metadata: { error },
        },
      );
      return null;
    }
  }

  /**
   * Nettoie les brouillons et données temporaires
   */
  static cleanup(): void {
    if (typeof window === "undefined") return;

    try {
      localStorage.removeItem("doodates-draft");
    } catch (error) {
      logError(
        ErrorFactory.storage(
          "Error cleaning up draft data",
          "Erreur lors du nettoyage",
        ),
        {
          component: "PollCreatorService",
          operation: "cleanup",
          metadata: { error },
        },
      );
    }
  }
}
